import { useEffect, useState, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { getProfile } from '../../services/profileService';
import { schemeForEmployee } from '../../utils/deptColors';
import { trackAction } from '../../services/gamificationService';
import { useAuth } from '../../hooks/useAuth';
import { EditProfileModal } from '../EditProfileModal/EditProfileModal';
import { BadgeCelebration } from '../BadgeCelebration/BadgeCelebration';
import { BadgeShowcase } from '../BadgeShowcase/BadgeShowcase';
import { BadgeDetail } from '../BadgeShowcase/BadgeDetail';
import { ProfileRail } from './ProfileRail';
import { ProfileCard } from './ProfileCard';
import { TenureChip } from '../TenureBadge/TenureBadge';
import { teamsChatUrl, teamsCallUrl, telUrl, mailtoUrl } from '../../utils/deepLinks';
import { certStatus, certStatusLabel, CERT_STATUS } from '../../utils/certStatus';
import { parseHireDate } from '../../utils/hireDate';
import { Medallion } from '../awards/AwardBadge';
import { AlertTriangle, Clock, X, MapPin, MessageSquare, Phone, Mail, Star, Pencil } from 'lucide-react';
import { OrgChartEditModal } from '../OrgChart/OrgChartEditModal';

export function ProfileModal({ employee, photo, onClose, onSelectEmployee, photos, allEmployees = [], departments = [], currentUserId, onProfileUpdate, isFavorite, onToggleFavorite, isAdmin = false, onExclude, onViewInOrgChart, initialSection = 'overview', originX = 0, originY = 0 }) {
  const { account } = useAuth();
  const [customProfile, setCustomProfile] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOrgEditModal, setShowOrgEditModal] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  const [celebrationBadge, setCelebrationBadge] = useState(null);
  const [celebrationType, setCelebrationType] = useState('achievement');
  // Inspecting a badge already held — a separate, quiet surface
  const [inspectBadge, setInspectBadge] = useState(null);
  const [activeSection, setActiveSection] = useState(initialSection);

  const profileSections = [
    { id: 'overview', label: 'Overview' },
    { id: 'expertise', label: 'Expertise' },
    { id: 'team', label: 'Team' },
    { id: 'badges', label: 'Trophy Case' },
  ];

  const isOwnProfile = currentUserId === employee?.id || account?.localAccountId === employee?.id;

  // Scroll-spy: with every section in the document, the bar has to follow the
  // scroll position rather than a click, or it lies as soon as you scroll.
  const bodyScrollRef = useRef(null);
  useEffect(() => {
    const root = bodyScrollRef.current;
    if (!root || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible) setActiveSection(visible.target.id.replace('profile-', ''));
      },
      { root, rootMargin: '-8px 0px -70% 0px', threshold: 0 },
    );
    for (const section of root.querySelectorAll('section[id^="profile-"]')) {
      observer.observe(section);
    }
    return () => observer.disconnect();
  }, [employee?.id, customProfile]);

  const presenceDotClass = (() => {
    const status = (employee?.availability || '').toLowerCase();
    if (status === 'available' || status === 'availableidle') return 'bg-emerald-500';
    if (status === 'busy' || status === 'busyidle') return 'bg-red-500';
    if (status === 'away' || status === 'berightback' || status === 'brb') return 'bg-yellow-500';
    if (status === 'donotdisturb' || status === 'dnd') return 'bg-red-600';
    return 'bg-slate-400';
  })();

  // Earn-moment only. BadgeCelebration defaults badgeType to 'tenure', so an
  // achievement must say so or it renders as a tenure tier.
  const triggerCelebration = (badge, badgeType = 'achievement') => {
    setCelebrationBadge(badge);
    setCelebrationType(badgeType);
    setShowCelebration(true);
  };

  useEffect(() => {
    if (employee) {
      const profile = getProfile(employee.id);
      setCustomProfile(profile);
      setActiveSection(initialSection);
      // Viewing a colleague counts toward the Profile Viewer badge.
      const viewerId = currentUserId || account?.localAccountId;
      if (viewerId && viewerId !== employee.id) {
        const badge = trackAction(viewerId, 'view_profile');
        if (badge) triggerCelebration(badge);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee]);

  // Direct reports come from the corrections-aware roster already in memory —
  // the same source of truth the org chart uses — so admin manager overrides
  // and exclusions flow through automatically. No extra Graph round-trip.
  const directReports = useMemo(
    () => (employee ? allEmployees.filter((e) => e.managerId === employee.id) : []),
    [employee, allEmployees],
  );

  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape' && !showEditModal) onClose(); };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, showEditModal]);

  const handleProfileSave = (profileData) => {
    setCustomProfile(profileData);
    onProfileUpdate?.();
  };

  if (!employee) return null;

  const displayPhoto = customProfile?.photo || photo;

  const initials = employee.displayName
    ?.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  // Avatar fallback uses the employee's department accent so profile avatars
  // match the card/org-chart color identity.
  const avatarScheme = schemeForEmployee(employee);

  // Placeholder content for empty profiles: nearby colleagues (same dept,
  // falling back to same office). Retires automatically once the person has
  // real profile content.
  const nearbyColleagues = (() => {
    const sameDept = employee.department
      ? allEmployees.filter((e) => e.id !== employee.id && e.department === employee.department)
      : [];
    const pool = sameDept.length > 0
      ? sameDept
      : employee.officeLocation
        ? allEmployees.filter((e) => e.id !== employee.id && e.officeLocation === employee.officeLocation)
        : [];
    return pool.slice(0, 6);
  })();

  const hasOverviewContent = Boolean(
    customProfile?.coreExpertise || customProfile?.bio || employee.bio || customProfile?.askMeAbout
    || customProfile?.openToSpeaking || customProfile?.openToWhitePaper || customProfile?.openToMentoring
    || customProfile?.interests?.length
  );
  const hasExpertiseContent = Boolean(
    customProfile?.areasOfExpertise?.length || customProfile?.skills?.length || employee.skills?.length
    || customProfile?.programsExpertise?.length || customProfile?.certifications?.length
    || customProfile?.practiceArea || customProfile?.statesLicensed?.length
    || customProfile?.highSchoolAlmaMater || customProfile?.collegeAlmaMater || customProfile?.volunteerAt
    || customProfile?.moregroupProjects?.length || customProfile?.priorProjects
    || customProfile?.innovationTopics || employee.openAssetProjects?.length
  );


  // Gamification: actions are credited to the signed-in user (the actor),
  // not the profile being viewed. A newly earned badge pops the celebration.
  const selfId = currentUserId || account?.localAccountId;
  const trackAndCelebrate = (action, data) => {
    if (!selfId) return;
    const badge = trackAction(selfId, action, data);
    if (badge) triggerCelebration(badge);
  };

  const handleCallClick = () => {
    trackAndCelebrate('make_call');
    if (employee.mobilePhone) {
      window.location.href = telUrl(employee.mobilePhone) || '#';
    } else if (employee.businessPhones?.[0]) {
      window.location.href = telUrl(employee.businessPhones[0]) || '#';
    } else if (employee.mail) {
      {
        const url = teamsCallUrl(employee.mail);
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  const SectionLabel = ({ children }) => (
    <div className="flex items-center gap-3 mb-3">
      <span className="text-[10px] font-bold tracking-[0.18em] text-ink-3 uppercase whitespace-nowrap">{children}</span>
      <div className="flex-1 h-px bg-line" />
    </div>
  );

  // Counts shown in the jumpbar
  const sectionCounts = {
    expertise: [
      ...(customProfile?.skills ?? []),
      ...(customProfile?.areasOfExpertise ?? []),
      ...(employee.skills ?? []),
    ].filter((skill, i, arr) => arr.indexOf(skill) === i).length,
    team: directReports.length + (employee.manager ? 1 : 0),
    badges: undefined,
  };

  const hasContact = employee.mobilePhone || employee.businessPhones?.[0] || employee.mail;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        onClick={onClose}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        style={{ perspective: '1400px' }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.15, x: originX, y: originY, rotateY: -90 }}
          animate={{ opacity: 1, scale: 1, x: 0, y: 0, rotateY: 0 }}
          exit={{ opacity: 0, scale: 0.15, x: originX, y: originY, rotateY: 90 }}
          transition={{ type: 'spring', stiffness: 220, damping: 26 }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-modal-name"
          // Fixed height (not max-height) so the modal doesn't resize as tabs
          // with different content lengths are selected.
          className="bg-surface rounded-card shadow-e3 border border-line max-w-5xl w-full h-[min(760px,calc(90vh-env(safe-area-inset-bottom,0px)))] overflow-hidden flex flex-col"
        >

          {/* ── Page head ─────────────────────────────────────────────────
              Per the mockup's .pstrip / .pcrumb / .pid / .pacts. This replaced a
              ~200px gradient banner with a centre-stacked identity and four large
              action buttons: a hero treatment that pushed the actual content —
              contact, reporting line, skills — below the fold on every profile.
              The brand now appears as a 4px strip, identity reads left-aligned
              like a page title, and actions sit compactly on the right. */}
          <div className="flex-shrink-0 bg-surface border-b border-line">
            <div className="h-1" style={{ background: 'linear-gradient(90deg, var(--accent), #2DD4BF)' }} />

            {/* Breadcrumb — says where you are, which a floating hero never did */}
            <div className="flex items-center gap-[7px] px-5 pt-2.5 text-[11.5px] text-ink-3">
              <span>Directory</span>
              {employee.department && (
                <>
                  <span aria-hidden="true">/</span>
                  <span>{employee.department}</span>
                </>
              )}
              <button
                onClick={onClose}
                className="ml-auto p-1 rounded-full text-ink-4 hover:text-ink-2 transition-colors"
                aria-label="Close profile"
              >
                <X size={15} />
              </button>
            </div>

            {/* .pid — 88px avatar | identity | actions */}
            <div className="grid grid-cols-[64px_1fr] sm:grid-cols-[88px_1fr_auto] gap-4 sm:gap-[18px] items-start px-5 pt-3 pb-4">
              <div className="relative">
                {displayPhoto ? (
                  <img
                    src={displayPhoto}
                    alt=""
                    className="w-16 h-16 sm:w-[88px] sm:h-[88px] rounded-full object-cover object-top ring-1 ring-line-strong"
                  />
                ) : (
                  <div className="w-16 h-16 sm:w-[88px] sm:h-[88px] rounded-full bg-surface-2 ring-1 ring-line-strong flex items-center justify-center">
                    <span className="text-[22px] font-semibold text-ink-3 select-none">
                      {(employee.displayName || '?').split(/\s+/).map((n) => n[0]).join('').toUpperCase().slice(0, 2)}
                    </span>
                  </div>
                )}
                <span
                  className={`absolute bottom-1 right-1 w-3.5 h-3.5 rounded-full border-2 border-surface ${presenceDotClass}`}
                  title={employee.availability || 'Status unknown'}
                />
              </div>

              <div className="min-w-0">
                <h2
                  id="profile-modal-name"
                  className="text-[24px] font-extrabold tracking-[-0.02em] leading-[1.12] text-ink truncate"
                >
                  {employee.displayName}
                </h2>
                <p className="text-[14px] text-ink-2 mt-[3px] leading-[1.4]">
                  {employee.jobTitle || 'Employee'}
                </p>

                {/* .facts — inline chips rather than a centred pill row */}
                <div className="flex flex-wrap gap-2 mt-2.5">
                  {employee.department && (
                    <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-md bg-surface-2 border border-line text-[11.5px] font-semibold text-ink-2">
                      {employee.department}
                    </span>
                  )}
                  {employee.officeLocation && (
                    <span className="inline-flex items-center gap-1.5 h-6 px-2.5 rounded-md bg-surface-2 border border-line text-[11.5px] font-semibold text-ink-2">
                      <MapPin size={11} className="text-ink-4" />
                      {employee.officeLocation}
                    </span>
                  )}
                  <TenureChip hireDate={employee.hireDate} />
                </div>
              </div>

              {/* .pacts — compact, right-aligned */}
              <div className="col-span-2 sm:col-span-1 flex flex-wrap gap-2 sm:justify-end">
                {teamsChatUrl(employee.mail) && (
                  <a
                    href={teamsChatUrl(employee.mail)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-ctl bg-accent text-[var(--on-accent)]
                               text-[13px] font-semibold shadow-e1 hover:opacity-90 transition-opacity"
                  >
                    <MessageSquare size={14} />
                    Message
                  </a>
                )}
                {hasContact && (
                  <button
                    onClick={handleCallClick}
                    className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-ctl bg-surface border border-line
                               text-[13px] font-semibold text-ink-2 hover:border-accent-line transition-colors"
                  >
                    <Phone size={14} />
                    Call
                  </button>
                )}
                {mailtoUrl(employee.mail) && (
                  <a
                    href={mailtoUrl(employee.mail)}
                    title="Email"
                    aria-label="Email"
                    className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-ctl bg-surface border border-line
                               text-ink-2 hover:border-accent-line transition-colors"
                  >
                    <Mail size={14} />
                  </a>
                )}
                {onToggleFavorite && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggleFavorite(employee.id); }}
                    title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    className={`inline-flex items-center justify-center w-[34px] h-[34px] rounded-ctl bg-surface border border-line
                                transition-colors ${isFavorite ? 'text-amber-400' : 'text-ink-4 hover:text-amber-400'}`}
                  >
                    <Star size={14} fill={isFavorite ? 'currentColor' : 'none'} />
                  </button>
                )}
                {isOwnProfile && (
                  <button
                    onClick={() => setShowEditModal(true)}
                    title="Edit profile"
                    aria-label="Edit profile"
                    className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-ctl bg-surface border border-line
                               text-ink-2 hover:border-accent-line transition-colors"
                  >
                    <Pencil size={14} />
                  </button>
                )}
                {isAdmin && !isOwnProfile && (
                  <button
                    onClick={() => setShowOrgEditModal(true)}
                    title="Edit reporting structure and title"
                    aria-label="Edit reporting structure and title"
                    className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-ctl bg-surface border border-line
                               text-ink-2 hover:border-accent-line transition-colors"
                  >
                    <Pencil size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>


          {/* ── .jumpbar ──────────────────────────────────────────────────
              Anchors, not tabs. Every section is present in the document, so a
              deep link lands somewhere real, Cmd+F finds text in sections you are
              not "on", and printing produces a whole profile rather than one
              quarter of one. Counts come from the data so the bar says how much
              is there before you scroll. */}
          <nav
            className="flex gap-0.5 px-5 bg-surface border-b border-line flex-shrink-0 overflow-x-auto"
            style={{ scrollbarWidth: 'none' }}
            aria-label="Profile sections"
          >
            {profileSections.map((section) => (
              <button
                key={section.id}
                onClick={() => {
                  setActiveSection(section.id);
                  document
                    .getElementById(`profile-${section.id}`)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                {...(activeSection === section.id ? { 'aria-current': 'true' } : {})}
                className={`inline-flex items-center gap-[7px] px-3 py-2.5 text-[13px] font-semibold whitespace-nowrap
                  border-b-2 transition-colors
                  ${activeSection === section.id
                    ? 'text-accent border-accent'
                    : 'text-ink-3 border-transparent hover:text-ink-2'}`}
              >
                {section.label}
                {typeof sectionCounts[section.id] === 'number' && sectionCounts[section.id] > 0 && (
                  <span className="font-mono text-[10px] text-ink-3">{sectionCounts[section.id]}</span>
                )}
              </button>
            ))}
          </nav>

          {/* ── Body: scrollable sections + persistent rail ──────────────
              Contact used to live inside Overview and the reporting line inside
              Team, so the two things people open a profile for both vanished the
              moment they looked at anything else. */}
          <div ref={bodyScrollRef} className="flex-1 overflow-y-auto bg-canvas min-h-0">
          <div className="grid gap-[18px] px-5 py-[18px] items-start min-[900px]:grid-cols-[1fr_306px]">
          <div className="flex flex-col gap-4 min-w-0">

            {/* ── OVERVIEW ──────────────────────────────────── */}
            <section id="profile-overview" className="scroll-mt-2">
            <ProfileCard title="About" source="self">
            <div className="space-y-6">

            {/* Contact — always available from directory data, so the
                Overview tab has substance even before a profile is built. */}
            {/* lg:hidden — the rail carries this at wide widths; below lg the rail
                is hidden, so Overview keeps its own copy. */}
            {(employee.mail || employee.mobilePhone || employee.businessPhones?.[0] || employee.officeLocation || employee.hireDate) && (
              <div className="lg:hidden">
                <SectionLabel>Contact</SectionLabel>
                <div className="space-y-2.5">
                  {employee.mail && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-ink-3 w-28 flex-shrink-0">Email</span>
                      <a href={mailtoUrl(employee.mail)} className="text-sm text-accent hover:underline truncate">{employee.mail}</a>
                    </div>
                  )}
                  {employee.mobilePhone && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-ink-3 w-28 flex-shrink-0">Mobile</span>
                      <a href={telUrl(employee.mobilePhone)} className="text-sm text-ink-2 hover:underline">{employee.mobilePhone}</a>
                    </div>
                  )}
                  {employee.businessPhones?.[0] && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-ink-3 w-28 flex-shrink-0">Office Phone</span>
                      <a href={telUrl(employee.businessPhones[0])} className="text-sm text-ink-2 hover:underline">{employee.businessPhones[0]}</a>
                    </div>
                  )}
                  {employee.officeLocation && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-ink-3 w-28 flex-shrink-0">Office</span>
                      <span className="text-sm text-ink-2">{employee.officeLocation}</span>
                    </div>
                  )}
                  {parseHireDate(employee.hireDate) && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-ink-3 w-28 flex-shrink-0">Joined</span>
                      <span className="text-sm text-ink-2">
                        {parseHireDate(employee.hireDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {customProfile?.coreExpertise && (
              <div className="px-4 py-3 bg-accent-soft rounded-xl border border-accent-line">
                <p className="text-ink-2 font-medium italic text-sm">"{customProfile.coreExpertise}"</p>
              </div>
            )}

            {(customProfile?.bio || employee.bio) && (
              <div>
                <SectionLabel>About</SectionLabel>
                <p className="text-sm text-ink-2 leading-relaxed whitespace-pre-wrap">{customProfile?.bio || employee.bio}</p>
              </div>
            )}

            {customProfile?.askMeAbout && (
              <div className="p-3 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-800/40">
                <p className="text-[10px] font-bold tracking-[0.15em] text-amber-700 dark:text-amber-300 uppercase mb-1.5">Ask Me About</p>
                <p className="text-sm text-ink-2">{customProfile.askMeAbout}</p>
              </div>
            )}

            {(customProfile?.openToSpeaking || customProfile?.openToWhitePaper || customProfile?.openToMentoring) && (
              <div>
                <SectionLabel>Open To</SectionLabel>
                <div className="flex flex-wrap gap-2">
                  {customProfile.openToSpeaking && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-full text-xs font-medium border border-amber-200 dark:border-amber-700/40">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
                      Public Speaking
                    </span>
                  )}
                  {customProfile.openToWhitePaper && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-400 rounded-full text-xs font-medium border border-sky-200 dark:border-sky-700/40">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      White Paper
                    </span>
                  )}
                  {customProfile.openToMentoring && (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 rounded-full text-xs font-medium border border-emerald-200 dark:border-emerald-700/40">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                      Mentoring
                    </span>
                  )}
                </div>
                {customProfile.openToMentoring && customProfile.mentorTopics && (
                  <p className="mt-2 text-xs text-ink-3">{customProfile.mentorTopics}</p>
                )}
              </div>
            )}

            {customProfile?.interests?.length > 0 && (
              <div>
                <SectionLabel>Interests</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                  {customProfile.interests.map((interest) => (
                    <span key={interest} className="px-2.5 py-1 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-400 rounded-full text-xs font-medium">{interest}</span>
                  ))}
                </div>
              </div>
            )}

            {!hasOverviewContent && !isOwnProfile && (
              <div className="text-center px-6 py-8 rounded-2xl bg-surface-2 border border-line">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-accent-soft flex items-center justify-center">
                  <svg className="w-5 h-5 text-accent" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 3l1.9 5.7a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-2a2 2 0 0 0 1.3-1.3L12 3z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-ink-2">
                  {employee.displayName?.split(' ')[0]} hasn't built their profile yet
                </p>
                <p className="text-xs mt-1 max-w-xs mx-auto text-ink-3">
                  Their skills, projects, and story will appear here once they add them.
                </p>
                {employee.mail && (
                  <a
                    href={teamsChatUrl(employee.mail)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => trackAndCelebrate('send_message')}
                    className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold text-accent border border-accent-line hover:bg-accent-soft transition-colors"
                  >
                    Say hi on Teams
                  </a>
                )}
              </div>
            )}

            {/* Placeholder while the profile is empty: nearby colleagues.
                Disappears once real profile content takes its place. */}
            {!hasOverviewContent && nearbyColleagues.length > 0 && (
              <div>
                <SectionLabel>
                  More from {employee.department || employee.officeLocation || 'the team'}
                </SectionLabel>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {nearbyColleagues.map((colleague) => {
                    const scheme = schemeForEmployee(colleague);
                    const cPhoto = photos?.[colleague.id] || colleague.openAssetPhoto;
                    return (
                      <button
                        key={colleague.id}
                        onClick={() => onSelectEmployee?.(colleague)}
                        className="flex items-center gap-2.5 p-2.5 rounded-xl bg-surface-2 hover:bg-surface transition-colors text-left border border-line"
                      >
                        {cPhoto ? (
                          <img src={cPhoto} alt="" className="w-9 h-9 rounded-full object-cover object-top flex-shrink-0" />
                        ) : (
                          <span
                            className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                            style={{ background: scheme.accent }}
                          >
                            {(colleague.displayName || '?').split(/\s+/).map((p) => p[0]).join('').toUpperCase().slice(0, 2)}
                          </span>
                        )}
                        <span className="min-w-0">
                          <span className="block text-xs font-semibold text-ink-2 truncate">{colleague.displayName}</span>
                          <span className="block text-[11px] text-ink-3 truncate">{colleague.jobTitle || 'Employee'}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {isOwnProfile && (!customProfile?.bio && !customProfile?.skills?.length) && (
              <div className="p-4 bg-accent-soft border border-accent-line rounded-xl text-center">
                <p className="text-accent text-sm mb-3">Complete your profile to help colleagues learn more about you!</p>
                <button onClick={() => setShowEditModal(true)}
                  className="px-4 py-2 bg-accent text-[var(--on-accent)] rounded-lg hover:opacity-90 transition-opacity text-sm font-medium">
                  Add Bio, Skills & More
                </button>
              </div>
            )}

            </div>
            </ProfileCard>
            </section>

            {/* ── EXPERTISE ─────────────────────────────────── */}
            <section id="profile-expertise" className="scroll-mt-2">
            <ProfileCard title="Expertise" source="self">
            <div className="space-y-6">

            {!hasExpertiseContent && (
              <div className="text-center px-6 py-8 rounded-2xl bg-slate-50 dark:bg-zinc-800/60">
                <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-teal-50 dark:bg-teal-900/25 flex items-center justify-center">
                  <svg className="w-5 h-5 text-teal-600 dark:text-teal-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
                  </svg>
                </div>
                <p className="text-sm font-semibold text-slate-700 dark:text-zinc-200">
                  {isOwnProfile ? 'No skills listed yet' : 'No skills or expertise listed yet'}
                </p>
                <p className="text-xs mt-1 max-w-xs mx-auto text-slate-400 dark:text-zinc-500">
                  {isOwnProfile
                    ? 'Add your skills, software, and project experience so colleagues can find you.'
                    : `Once ${employee.displayName?.split(' ')[0]} tags their skills and software, they'll show here.`}
                </p>
                {isOwnProfile && (
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="mt-4 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-primary-500 hover:bg-primary-600 text-white transition-colors"
                  >
                    Add Skills & Expertise
                  </button>
                )}
              </div>
            )}

            {(customProfile?.areasOfExpertise?.length > 0 || customProfile?.skills?.length > 0 || customProfile?.programsExpertise?.length > 0 || employee.skills?.length > 0) && (
              <div>
                <SectionLabel>Skills & Expertise</SectionLabel>
                {customProfile?.areasOfExpertise?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {customProfile.areasOfExpertise.map((tag) => (
                      <span key={tag} className="px-2.5 py-1 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 rounded-full text-xs font-medium border border-teal-100 dark:border-teal-700/40">{tag}</span>
                    ))}
                  </div>
                )}
                {(customProfile?.skills?.length > 0 || employee.skills?.length > 0) && (
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {/* Self-listed skills first, then firm-curated (OpenAsset) skills not already listed */}
                    {[...(customProfile?.skills ?? []),
                      ...(employee.skills ?? []).filter((s) => !(customProfile?.skills ?? []).includes(s)),
                    ].map((skill) => (
                      <span key={skill} className="px-2.5 py-1 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300 rounded-full text-xs font-medium">{skill}</span>
                    ))}
                  </div>
                )}
                {customProfile?.programsExpertise?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {customProfile.programsExpertise.map((prog) => (
                      <span key={prog} className="px-2.5 py-1 bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-400 rounded-full text-xs font-medium">{prog}</span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {(customProfile?.practiceArea || customProfile?.statesLicensed?.length > 0 || customProfile?.highSchoolAlmaMater || customProfile?.collegeAlmaMater || customProfile?.volunteerAt) && (
              <div>
                <SectionLabel>Background</SectionLabel>
                <div className="space-y-2.5">
                  {customProfile.practiceArea && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 dark:text-zinc-500 w-28 flex-shrink-0">Practice Area</span>
                      <span className="text-sm text-slate-700 dark:text-zinc-200 font-medium">{customProfile.practiceArea}</span>
                    </div>
                  )}
                  {customProfile.statesLicensed?.length > 0 && (
                    <div className="flex items-start gap-3">
                      <span className="text-xs text-slate-400 dark:text-zinc-500 w-28 flex-shrink-0 pt-0.5">Licensed In</span>
                      <div className="flex flex-wrap gap-1">
                        {customProfile.statesLicensed.map((s) => (
                          <span key={s} className="px-2 py-0.5 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded text-xs font-medium">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {customProfile.collegeAlmaMater && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 dark:text-zinc-500 w-28 flex-shrink-0">College</span>
                      <span className="text-sm text-slate-700 dark:text-zinc-200">{customProfile.collegeAlmaMater}</span>
                    </div>
                  )}
                  {customProfile.highSchoolAlmaMater && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 dark:text-zinc-500 w-28 flex-shrink-0">High School</span>
                      <span className="text-sm text-slate-700 dark:text-zinc-200">{customProfile.highSchoolAlmaMater}</span>
                    </div>
                  )}
                  {customProfile.volunteerAt && (
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 dark:text-zinc-500 w-28 flex-shrink-0">Volunteers At</span>
                      <span className="text-sm text-slate-700 dark:text-zinc-200">{customProfile.volunteerAt}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {(customProfile?.moregroupProjects?.length > 0 || customProfile?.priorProjects || employee.openAssetProjects?.length > 0) && (
              <div>
                <SectionLabel>Projects</SectionLabel>
                {employee.openAssetProjects?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {employee.openAssetProjects.map((proj) => {
                      const name = typeof proj === 'string' ? proj : proj?.name;
                      return name ? (
                        <span key={name} className="px-2.5 py-1 bg-slate-100 dark:bg-zinc-800 text-slate-600 dark:text-zinc-300 rounded-full text-xs font-medium">{name}</span>
                      ) : null;
                    })}
                  </div>
                )}
                {customProfile?.moregroupProjects?.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    {customProfile.moregroupProjects.map((proj) => (
                      <div key={proj.id} className="flex items-center gap-3 p-2.5 bg-slate-50 dark:bg-zinc-800 rounded-lg">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-slate-800 dark:text-zinc-100">{proj.name}</p>
                          {(proj.role || proj.year) && (
                            <p className="text-xs text-slate-500 dark:text-zinc-400">{[proj.role, proj.year].filter(Boolean).join(' · ')}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {customProfile.priorProjects && (
                  <div className="p-3 bg-slate-50 dark:bg-zinc-800 rounded-xl">
                    <p className="text-[10px] font-bold tracking-wide text-slate-400 dark:text-zinc-500 uppercase mb-1.5">Prior to MOREgroup</p>
                    <p className="text-sm text-slate-700 dark:text-zinc-300">{customProfile.priorProjects}</p>
                  </div>
                )}
              </div>
            )}

            {customProfile?.innovationTopics && (
              <div className="p-4 bg-gradient-to-br from-violet-50 dark:from-violet-900/20 to-indigo-50 dark:to-indigo-900/20 rounded-xl border border-violet-100 dark:border-violet-700/40">
                <p className="text-[10px] font-bold tracking-[0.15em] text-violet-700 dark:text-violet-400 uppercase mb-1.5">Exploring</p>
                <p className="text-sm text-slate-700 dark:text-zinc-300">{customProfile.innovationTopics}</p>
              </div>
            )}

            {customProfile?.certifications?.length > 0 && (
              <div>
                <SectionLabel>Licenses &amp; Certifications</SectionLabel>
                {/* .certrow — 32px seal, name and issuer, expiry state on the right.
                    These were amber-tinted cards with a tick icon, which read as
                    "verified" for every row including the lapsed ones. */}
                <div>
                  {customProfile.certifications.map((cert) => {
                    const state = cert.expires ? certStatus(cert.expires) : null;
                    const label = state ? certStatusLabel(state) : null;
                    const expired = state?.status === CERT_STATUS.EXPIRED;
                    const tone = {
                      [CERT_STATUS.EXPIRED]: 'bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 border-red-300 dark:border-red-800',
                      [CERT_STATUS.EXPIRING]: 'bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-300 border-amber-300 dark:border-amber-800',
                      [CERT_STATUS.CURRENT]: 'bg-surface-2 text-ink-3 border-line',
                    }[state?.status];
                    const Icon = expired ? AlertTriangle : Clock;
                    return (
                      <div
                        key={cert.id}
                        className="grid grid-cols-[32px_minmax(0,1fr)] sm:grid-cols-[32px_minmax(0,1fr)_auto]
                                   gap-[11px] items-center py-[9px] border-b border-dashed border-line last:border-b-0"
                      >
                        {/* A maintained licence (one with a renewal date) is brass;
                            a certification you hold outright is steel; a lapsed one
                            is the dashed unearned outline, because it is no longer a
                            credential you can be staffed on. */}
                        <Medallion
                          size={32}
                          metal={cert.expires ? 'brass' : 'steel'}
                          state={expired ? 'locked' : 'earned'}
                          title={expired ? `${cert.name} — lapsed` : cert.name}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
                               strokeLinecap="round" strokeLinejoin="round" className="w-full h-full">
                            <circle cx="12" cy="9" r="5.2" />
                            <path d="M9.4 13.6 8.5 21l3.5-2 3.5 2-.9-7.4" />
                          </svg>
                        </Medallion>

                        <div className="min-w-0">
                          <p className="text-[12.5px] font-semibold text-ink leading-[1.3] truncate" title={cert.name}>
                            {cert.name}
                          </p>
                          <p className="text-[11px] text-ink-3 mt-0.5 truncate">
                            {[cert.issuer, cert.date ? `Issued ${cert.date}` : null].filter(Boolean).join(' · ')}
                          </p>
                        </div>

                        {label && (
                          <span className={`justify-self-start sm:justify-self-end col-start-2 sm:col-start-3
                            inline-flex items-center gap-1 px-[7px] py-[3px] rounded-[5px]
                            font-mono text-[10.5px] font-semibold border whitespace-nowrap ${tone}`}>
                            {state.status !== CERT_STATUS.CURRENT && <Icon size={10} />}
                            {label}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            </div>
            </ProfileCard>
            </section>

            {/* ── TEAM ──────────────────────────────────────── */}
            <section id="profile-team" className="scroll-mt-2">
            <ProfileCard title="Team" source="aad">
            <div className="space-y-6">

            {!(employee.manager || directReports.length > 0) && (
              <div className="text-center py-10">
                <p className="text-sm text-slate-400 dark:text-zinc-500">No reporting relationships on record.</p>
                {onViewInOrgChart && (
                  <button
                    onClick={() => onViewInOrgChart(employee)}
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                    </svg>
                    View in Org Chart
                  </button>
                )}
              </div>
            )}

            {(employee.manager || directReports.length > 0) && (
              <div>
                {/* Section header with the org-chart jump as an inline action */}
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-[10px] font-bold tracking-[0.18em] text-slate-400 dark:text-zinc-500 uppercase whitespace-nowrap">Org Hierarchy</span>
                  <div className="flex-1 h-px bg-slate-200 dark:bg-zinc-800" />
                  {onViewInOrgChart && (
                    <button
                      onClick={() => onViewInOrgChart(employee)}
                      className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors whitespace-nowrap"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                      </svg>
                      View in Org Chart
                    </button>
                  )}
                </div>
                <div className="space-y-1.5">
                  {employee.manager && (
                    <>
                      <button
                        onClick={() => onSelectEmployee?.(employee.manager)}
                        className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 dark:bg-zinc-800 hover:bg-slate-100 dark:hover:bg-zinc-700 transition-colors w-full text-left"
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                          style={{ background: schemeForEmployee(employee.manager).accent }}
                        >
                          {employee.manager.displayName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <div className="overflow-hidden flex-1">
                          <p className="text-xs font-medium text-slate-700 dark:text-zinc-200 truncate">{employee.manager.displayName}</p>
                          <p className="text-[10px] text-slate-400 dark:text-zinc-400 truncate">{employee.manager.jobTitle || 'Manager'}</p>
                        </div>
                        <span className="text-[10px] text-slate-400 dark:text-zinc-500 flex-shrink-0 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-zinc-700">Manager</span>
                      </button>
                      <div className="flex justify-center"><div className="w-px h-4 bg-slate-200 dark:bg-zinc-700" /></div>
                    </>
                  )}

                  <div className="flex items-center gap-3 p-2.5 rounded-xl bg-primary-50 dark:bg-primary-900/20 ring-1 ring-primary-200 dark:ring-primary-700/40">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold ring-2 ring-primary-400 flex-shrink-0"
                      style={{ background: avatarScheme.accent }}
                    >
                      {initials}
                    </div>
                    <div className="overflow-hidden flex-1">
                      <p className="text-xs font-semibold text-slate-800 dark:text-zinc-100 truncate">{employee.displayName}</p>
                      <p className="text-[10px] text-slate-500 dark:text-zinc-400 truncate">{employee.jobTitle}</p>
                    </div>
                  </div>

                  {directReports.length > 0 && (
                    <>
                      <div className="flex justify-center"><div className="w-px h-4 bg-slate-200 dark:bg-zinc-700" /></div>
                      <div className="space-y-1.5">
                        {directReports.slice(0, 4).map((report) => (
                          <button key={report.id} onClick={() => onSelectEmployee?.(report)}
                            className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 dark:bg-zinc-800 hover:bg-slate-100 dark:hover:bg-zinc-700 transition-colors w-full text-left">
                            {photos?.[report.id] ? (
                              <img src={photos[report.id]} alt={report.displayName} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold flex-shrink-0"
                                style={{ background: schemeForEmployee(report).accent }}
                              >
                                {report.displayName?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </div>
                            )}
                            <div className="overflow-hidden flex-1">
                              <p className="text-xs font-medium text-slate-700 dark:text-zinc-200 truncate">{report.displayName}</p>
                              <p className="text-[10px] text-slate-400 dark:text-zinc-400 truncate">{report.jobTitle}</p>
                            </div>
                            <span className="text-[10px] text-slate-400 dark:text-zinc-500 flex-shrink-0 px-2 py-0.5 rounded-full bg-slate-100 dark:bg-zinc-700">Report</span>
                          </button>
                        ))}
                        {directReports.length > 4 && (
                          <p className="text-xs text-center text-slate-400 dark:text-zinc-500 py-1">+{directReports.length - 4} more</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            </div>
            </ProfileCard>
            </section>

            {/* ── TROPHY CASE ───────────────────────────────── */}
            <section id="profile-badges" className="scroll-mt-2">
            <ProfileCard title="Trophy case" source="auto" allowOverflow>
              <BadgeShowcase
                userId={employee.id}
                hireDate={employee.hireDate}
                onBadgeClick={(badge) => setInspectBadge(badge)}
              />
            </ProfileCard>
            </section>

          </div>

          <ProfileRail
            employee={employee}
            manager={employee.manager}
            directReports={directReports}
            onSelectEmployee={onSelectEmployee}
            onViewInOrgChart={onViewInOrgChart}
          />
          </div>
          </div>
        </motion.div>
      </motion.div>

      {showEditModal && (
        <EditProfileModal
          employee={employee}
          manager={employee.manager}
          isAdmin={isAdmin}
          onClose={() => setShowEditModal(false)}
          onSave={handleProfileSave}
        />
      )}

      {showOrgEditModal && isAdmin && !isOwnProfile && (
        <OrgChartEditModal
          nodeId={employee.id}
          data={employee}
          photo={photo}
          allEmployees={allEmployees}
          departments={departments}
          onClose={() => setShowOrgEditModal(false)}
          onSaved={() => {
            setShowOrgEditModal(false);
            onProfileUpdate?.();
          }}
          onRemoved={() => {
            // Person is gone from the directory — close both modals and refresh.
            setShowOrgEditModal(false);
            onExclude?.();
            onClose();
          }}
        />
      )}

      <BadgeCelebration
        isOpen={showCelebration}
        onClose={() => setShowCelebration(false)}
        badge={celebrationBadge}
        badgeType={celebrationType}
        employeeName={employee?.displayName}
        showConfetti
      />

      {inspectBadge && (
        <BadgeDetail
          badge={inspectBadge}
          employeeName={employee?.displayName}
          hireDate={employee?.hireDate}
          onClose={() => setInspectBadge(null)}
        />
      )}
    </>
  );
}
